import React from 'react';
import { CheckCircle, AlertCircle, Info, AlertTriangle, X } from 'lucide-react';
import { useToast } from '../context/ToastContext';

/**
 * ToastContainer — renders the queued toasts in a fixed bottom-right
 * stack. Themed with brand tokens so it flips with the rest of the app.
 *
 * Mounted once near the app root, just inside <ToastProvider>.
 */
export default function ToastContainer() {
  const { items, dismiss } = useToast();

  if (!items || items.length === 0) return null;

  return (
    <div
      role="region"
      aria-label="Notifications"
      className="fixed z-[100] bottom-5 right-5 left-5 sm:left-auto flex flex-col gap-2.5 pointer-events-none"
      style={{ maxWidth: 'calc(100vw - 40px)', width: 380 }}
    >
      {items.map((t) => (
        <Toast key={t.id} item={t} onClose={() => dismiss(t.id)} />
      ))}
    </div>
  );
}

const TONES = {
  success: {
    icon: CheckCircle,
    iconBg: 'var(--mint-50)',
    iconColor: 'var(--mint-700)',
    accent:   'var(--mint-700)',
  },
  error: {
    icon: AlertCircle,
    iconBg: 'rgba(216, 122, 79, 0.12)',
    iconColor: '#A33A1F',
    accent:   '#A33A1F',
  },
  warning: {
    icon: AlertTriangle,
    iconBg: 'rgba(229, 180, 80, 0.18)',
    iconColor: '#8C661E',
    accent:   '#8C661E',
  },
  info: {
    icon: Info,
    iconBg: 'rgba(125, 165, 200, 0.18)',
    iconColor: '#5A8AAB',
    accent:   '#5A8AAB',
  },
};

function Toast({ item, onClose }) {
  const tone = TONES[item.tone] || TONES.info;
  const Icon = tone.icon;
  return (
    <div
      role="status"
      className="tk-toast pointer-events-auto flex items-start gap-3 p-3.5 rounded-2xl"
      style={{
        background: 'var(--surface-card)',
        border: '1px solid var(--surface-card-border)',
        boxShadow: '0 1px 0 rgba(255,255,255,0.6) inset, 0 22px 50px -22px rgba(20,32,27,0.30)',
        borderLeft: `3px solid ${tone.accent}`,
      }}
    >
      <span
        className="inline-flex items-center justify-center w-8 h-8 rounded-lg shrink-0 mt-0.5"
        style={{ background: tone.iconBg, color: tone.iconColor }}
      >
        <Icon size={15} />
      </span>
      <div className="flex-1 min-w-0">
        <div className="text-[13.5px] font-medium" style={{ color: 'var(--ink)' }}>
          {item.title}
        </div>
        {item.description && (
          <div className="text-[12px] mt-0.5" style={{ color: 'var(--ink-mute)' }}>
            {item.description}
          </div>
        )}
        {item.action && (
          <button
            type="button"
            onClick={() => { item.action.onClick(); onClose(); }}
            className="mt-2 text-[12px] font-medium inline-flex items-center"
            style={{ color: tone.accent }}
          >
            {item.action.label} →
          </button>
        )}
      </div>
      <button
        type="button"
        onClick={onClose}
        aria-label="Dismiss notification"
        className="shrink-0 p-1 rounded-md"
        style={{ color: 'var(--ink-mute)' }}
        onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--paper-2)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
      >
        <X size={13} />
      </button>
    </div>
  );
}
